import React from "react";
import { useForm } from "../../contexts/FormContext";
import { Resources } from "../../utils/resources";
import { useUser } from "../../contexts/UserContext";
import { Link } from "react-router-dom";
import editIcon from "../../assets/icons/edit-icon.svg";

interface Step8Props {
  onStepChange: (step: number) => void;
}

const Step8: React.FC<Step8Props> = ({ onStepChange }) => {
  const {
    formData,
    updateField,
    validateField,
    isFieldTouched,
    touchedFields,
  } = useForm();
  const { marketing_email_dnc_flag, marketing_sms_dnc_flag } = useUser();

  const transformFirstWord = (input: string): string => {
    if (!input) return "";
    if (input === "Pay off another loan") return "paying off another loan";

    const words = input.split(" ");
    if (words.length === 0) return input;

    words[0] = words[0].charAt(0).toLowerCase() + words[0].slice(1);
    return words.join(" ");
  };

  const getLoanPurposeLabel = (value: string): string => {
    const entry = Object.entries(Resources.useOfFunds).find(
      ([_, v]) => v === value,
    );
    return entry ? entry[0] : "miscellaneous expenses";
  };

  const calculateTotalExpenses = (): number => {
    const expenses = [
      "rent",
      "credit_commitments",
      "utility_bills",
      "transport",
      "food_drink",
      "other_expenses",
    ];
    return expenses.reduce((total, field) => {
      return total + (Number(formData[field]) || 0);
    }, 0);
  };

  const loanPurpose = transformFirstWord(
    getLoanPurposeLabel(formData.use_of_funds || ""),
  );
  const totalExpenses = calculateTotalExpenses();

  // Show errors if field is touched or if form submission was attempted
  const expenseCheckboxError =
    isFieldTouched("checkbox_expense") || touchedFields["checkbox_expense"]
      ? validateField("checkbox_expense", formData.checkbox_expense || "")
      : null;

  const privacyConsentError =
    isFieldTouched("consent_privacy") || touchedFields["consent_privacy"]
      ? validateField("consent_privacy", formData.consent_privacy || "")
      : null;

  const handleCheckboxChange =
    (type: "expense" | "privacy" | "marketing" | "sms") =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const checked = e.target.checked;

      // Map checkbox types to form field names
      const fieldMap = {
        expense: "checkbox_expense",
        privacy: "consent_privacy",
        marketing: "marketing_email_optin_flag",
        sms: "marketing_sms_optin_flag",
      };

      // Update form data
      updateField(fieldMap[type], checked.toString());
    };

  // Provide summary text on final step
  const summaryItems = [
    {
      title: `£${formData.borrow_amount} for ${loanPurpose}`,
      step: 0,
    },
    {
      title: `${formData.first_name} ${formData.last_name}`,
      step: 1,
    },
    {
      title: formData.postcode,
      step: 2,
    },
    {
      title: `Income: £${formData.income_amount}`,
      step: 5,
    },
    {
      title: `Expenses: £${totalExpenses}`,
      step: 6,
    },
  ];

  return (
    <div className="flex flex-col space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Almost there...</h1>
        <p className="mt-2">Confirm your application details are correct</p>
      </div>

      {/* Summary Cards */}
      <div className="bg-grey-20 rounded-8 overflow-hidden px-4 py-6 space-y-4">
        {summaryItems.map((item, index) => (
          <div key={index}>
            <div
              onClick={() => onStepChange(item.step)}
              className="cursor-pointer hover:bg-grey-40 transition-colors"
            >
              <div className="flex justify-between items-center">
                <span>{item.title}</span>
                <img alt="edit icon" src={editIcon} className="w-5 h-5" />
              </div>
            </div>
            {index < summaryItems.length - 1 && (
              <div className="border-b border-grey-60 mt-4"></div>
            )}
          </div>
        ))}
      </div>

      {/* Confirmation Checkboxes */}
      <div className="space-y-4">
        <div className="space-y-2">
          <label
            className={`flex items-start space-x-2 ${
              expenseCheckboxError ? "text-red" : ""
            }`}
          >
            <input
              type="checkbox"
              name="confirm_expense"
              checked={formData.checkbox_expense === "true"}
              onChange={handleCheckboxChange("expense")}
              className={`mt-1 ${expenseCheckboxError ? "border-red" : ""}`}
              aria-invalid={!!expenseCheckboxError}
              aria-describedby={
                expenseCheckboxError ? "expense-checkbox-error" : undefined
              }
            />
            <span className="text-sm text-secondary">
              I confirm that the income and expenses that I have provided are
              correct.
            </span>
          </label>
          {expenseCheckboxError && (
            <p
              className="mt-1 text-sm text-red"
              id="expense-checkbox-error"
              role="alert"
            >
              {expenseCheckboxError}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <label
            className={`flex items-start space-x-2 ${
              privacyConsentError ? "text-red" : ""
            }`}
          >
            <input
              type="checkbox"
              name="consent_privacy"
              checked={formData.consent_privacy === "true"}
              onChange={handleCheckboxChange("privacy")}
              className={`mt-1 ${privacyConsentError ? "border-red" : ""}`}
              aria-invalid={!!privacyConsentError}
              aria-describedby={
                privacyConsentError ? "privacy-consent-error" : undefined
              }
            />
            <span className="text-sm text-secondary">
              I understand that by proceeding, my data will be shared with
              Afforda's panel of lenders who may conduct a soft credit search.
              This search will not impact your credit score.
            </span>
          </label>
          {privacyConsentError && (
            <p
              className="mt-1 text-sm text-red"
              id="privacy-consent-error"
              role="alert"
            >
              {privacyConsentError}
            </p>
          )}
        </div>

        {/* Marketing Consent */}
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          {/* Marketing Consent Info */}
          <div className="mb-3 flex items-start gap-2">
            <svg
              className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
            <div className="text-sm">
              <strong className="text-secondary">Marketing communications from Afforda</strong>
              <p className="mt-1 text-xs text-gray-600">
                Gain Credit has a range of products and brands. These include Afforda. We want to share news and deals from all of them. We may also share offers from some brands we trust. You can unsubscribe at any time in your {" "}
                <Link
                  to="/preference-center"
                  className="text-blue-500 hover:underline"
                >
                  profile settings
                </Link>
                . Select how you'd like to hear from us:
              </p>
            </div>
          </div>

          {/* Email and SMS Consent Checkboxes */}
          <div className="space-y-3">
            {marketing_email_dnc_flag && (
              <label className="flex items-start space-x-2">
                <input
                  type="checkbox"
                  name="marketing_email_optin_flag"
                  checked={formData.marketing_email_optin_flag === "true"}
                  onChange={handleCheckboxChange("marketing")}
                  className="mt-1"
                />
                <span className="text-sm text-secondary">
                  Email
                </span>
              </label>
            )}

            {marketing_sms_dnc_flag && (
              <label className="flex items-start space-x-2">
                <input
                  type="checkbox"
                  name="marketing_sms_optin_flag"
                  checked={formData.marketing_sms_optin_flag === "true"}
                  onChange={handleCheckboxChange("sms")}
                  className="mt-1"
                />
                <span className="text-sm text-secondary">
                  SMS
                </span>
              </label>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step8;
